import motion from '@lib/motion';
import { listen } from '@theme/events';
import { SKY_THEME_EVENTS } from '@theme/config';
import { motionReduced, THEME_ERROR_ICON } from '@theme/theme-global';

if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();

        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);
        this.recipientFields = this.querySelector('.recipient-fields');
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`);
        this.checkboxInput.disabled = false;
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
        this.hiddenControlField.disabled = true;
        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();

        this.errorMessageWrapper = this.querySelector('[role="alert"]');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('p');
        this.errorMessageText =
          this.errorMessage?.querySelector('[data-error-heading]') || this.errorMessage;
        // textContent + trim: innerText keeps Liquid newlines, and setting innerText turns those into <br>
        this.defaultErrorHeader = this.errorMessageText?.textContent?.trim() || '';
        this.currentProductVariantId = this.dataset.productVariantId;
        this.fieldsAnimation = null;

        this.addEventListener('change', () => this.onChange(true));
        this.onChange(false);
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        this.cartUpdateUnsubscriber = listen(SKY_THEME_EVENTS.cartUpdate, (event) => {
          if (event.source === 'buy-form' && event.productVariantId?.toString() === this.currentProductVariantId) {
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = listen(SKY_THEME_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.dataset.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString();
          }
        });

        this.cartErrorUnsubscriber = listen(SKY_THEME_EVENTS.cartError, (event) => {
          if (event.source === 'buy-form' && event.productVariantId?.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, event.errors);
          }
        });
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
        this.variantChangeUnsubscriber?.();
        this.cartErrorUnsubscriber?.();
        this.fieldsAnimation?.stop?.();
      }

      onChange(animate = true) {
        if (this.checkboxInput.checked) {
          this.enableInputFields();
          this.expandFields(animate);
          if (this.recipientFieldsLiveRegion) {
            this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings?.recipientFormExpanded || '';
          }
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          this.collapseFields(animate);
          if (this.recipientFieldsLiveRegion) {
            this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings?.recipientFormCollapsed || '';
          }
        }
      }

      stopFieldsAnimation() {
        this.fieldsAnimation?.stop?.();
        this.fieldsAnimation = null;
      }

      // Expand animation locks height in px — release so errors / content can grow without overlapping ATC
      releaseFieldsLayout() {
        const el = this.recipientFields;
        if (!el) return;

        // Drop any leftover WAAPI/Motion fills that keep the open pixel height
        if (typeof el.getAnimations === 'function') {
          el.getAnimations().forEach((animation) => {
            try {
              animation.commitStyles?.();
            } catch (e) {
              // ignore
            }
            animation.cancel();
          });
        }

        el.style.height = 'auto';
        el.style.opacity = '';
        el.style.overflow = 'visible';
      }

      expandFields(animate = true) {
        const el = this.recipientFields;
        if (!el) return;

        el.classList.remove('hidden');
        el.setAttribute('aria-hidden', 'false');

        if (!animate || motionReduced()) {
          this.stopFieldsAnimation();
          this.releaseFieldsLayout();
          return;
        }

        this.stopFieldsAnimation();
        el.style.overflow = 'hidden';
        el.style.height = 'auto';
        el.style.opacity = '0';
        const endHeight = el.scrollHeight;
        el.style.height = '0px';

        this.fieldsAnimation = motion.animate(
          el,
          { height: ['0px', `${endHeight}px`], opacity: [0, 1] },
          { duration: 0.35, easing: [0.7, 0, 0.2, 1] }
        );

        this.fieldsAnimation.finished
          .then(() => {
            if (!this.checkboxInput.checked) return;
            this.releaseFieldsLayout();
            this.fieldsAnimation = null;
          })
          .catch(() => {
            this.fieldsAnimation = null;
          });
      }

      collapseFields(animate = true) {
        const el = this.recipientFields;
        if (!el) return;

        el.setAttribute('aria-hidden', 'true');

        if (!animate || motionReduced() || el.classList.contains('hidden')) {
          this.stopFieldsAnimation();
          el.classList.add('hidden');
          el.style.height = '';
          el.style.opacity = '';
          el.style.overflow = '';
          return;
        }

        this.stopFieldsAnimation();
        this.releaseFieldsLayout();
        const startHeight = el.scrollHeight;
        el.style.overflow = 'hidden';
        el.style.height = `${startHeight}px`;
        el.style.opacity = '1';

        this.fieldsAnimation = motion.animate(
          el,
          { height: [`${startHeight}px`, '0px'], opacity: [1, 0] },
          { duration: 0.3, easing: [0.7, 0, 0.2, 1] }
        );

        this.fieldsAnimation.finished
          .then(() => {
            if (this.checkboxInput.checked) return;
            el.classList.add('hidden');
            el.style.height = '';
            el.style.opacity = '';
            el.style.overflow = '';
            this.fieldsAnimation = null;
          })
          .catch(() => {
            this.fieldsAnimation = null;
          });
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput, this.sendonInput];
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => {
          if (field) field.value = '';
        });
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => {
          if (field) field.disabled = false;
        });
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => {
          if (field) field.disabled = true;
        });
      }

      fieldErrorElements() {
        return ['email', 'name', 'message', 'send_on'].map((key) =>
          this.querySelector(`#RecipientForm-${key}-error-${this.dataset.sectionId}`)
        );
      }

      inputForErrorKey(key) {
        if (key === 'send_on') return this.sendonInput;
        return this[`${key}Input`];
      }

      // Cart API often returns HTML like "can't be blank<br>" — strip tags for text display
      formatErrorMessage(value) {
        const raw = Array.isArray(value) ? value.join(', ') : String(value ?? '');
        return raw
          .replace(/<br\s*\/?>/gi, ' ')
          .replace(/<[^>]+>/g, '')
          .replace(/\s+/g, ' ')
          .trim();
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        if (!this.errorMessageWrapper) return;

        this.errorMessageWrapper.classList.remove('hidden');

        if (typeof body === 'object' && body !== null) {
          if (this.errorMessageText) this.errorMessageText.textContent = this.defaultErrorHeader;

          Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
            const fieldId = key === 'send_on' ? 'send-on' : key;
            const fieldSelector = `#Recipient-${fieldId}-${this.dataset.sectionId}`;
            const message = this.formatErrorMessage(value);
            if (!message) return;

            const errorMessageElement = this.querySelector(`#${errorMessageId}`);
            if (!errorMessageElement) return;

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
            }

            const fieldText = errorMessageElement.querySelector('[data-error-text]');
            const formatted = message.endsWith('.') ? message : `${message}.`;
            if (fieldText) {
              fieldText.textContent = formatted;
            } else {
              errorMessageElement.textContent = formatted;
            }
            errorMessageElement.classList.remove('hidden');

            const inputElement = this.inputForErrorKey(key);
            if (!inputElement) return;

            inputElement.setAttribute('aria-invalid', true);
            inputElement.setAttribute('aria-describedby', errorMessageId);
          });
        } else if (this.errorMessageText) {
          this.errorMessageText.textContent = this.formatErrorMessage(body);
        }

        // Error blocks were display:none — unlock open-height so fields push ATC buttons down
        this.releaseFieldsLayout();
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.className = 'd-flex align-items-center gap-1 form-message-small form-message-error';
        a.insertAdjacentHTML('afterbegin', THEME_ERROR_ICON());
        a.appendChild(document.createTextNode(message));
        li.appendChild(a);
        return li;
      }

      clearErrorMessage() {
        if (this.errorMessageWrapper) {
          this.errorMessageWrapper.classList.add('hidden');
        }

        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.fieldErrorElements().forEach((field) => {
          if (!field) return;
          field.classList.add('hidden');
          const fieldText = field.querySelector('[data-error-text]');
          if (fieldText) {
            fieldText.textContent = '';
          } else {
            field.textContent = '';
          }
        });

        [this.emailInput, this.messageInput, this.nameInput, this.sendonInput].forEach((inputElement) => {
          if (!inputElement) return;
          inputElement.setAttribute('aria-invalid', false);
          inputElement.removeAttribute('aria-describedby');
        });

        if (this.checkboxInput?.checked) this.releaseFieldsLayout();
      }

      resetRecipientForm() {
        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.onChange(true);
        }
      }
    }
  );
}
